import { HugeiconsIcon } from '@hugeicons/react';
import {
  ArtificialIntelligence05Icon,
  AiMagicIcon,
  BotIcon,
  Calendar01Icon,
  CpuIcon,
  FileValidationIcon,
  GitBranchIcon,
  GlobeIcon,
  GridViewIcon,
  ServerStack01Icon,
  Shield01Icon,
  WorkflowCircle06Icon,
} from '@hugeicons/core-free-icons';
import { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router';
import { api } from '@/lib/api';
import { StatusBadge, RiskBadge } from '@/components/common/Bits';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { MotionSection, StaggerContainer, StaggerItem } from '@/components/shared/MotionSection';
import { cn } from '@/lib/utils';

const KINDS = [
  {
    key: 'prompts',
    label: 'Prompts',
    icon: AiMagicIcon,
    position: [52, 16],
    href: '/toolkit/prompts',
    description: 'Instrucciones reutilizables publicadas por la comunidad.',
    surface: 'bg-primary/10',
    border: 'border-primary/25',
    text: 'text-primary',
  },
  {
    key: 'skills',
    label: 'Skills',
    icon: WorkflowCircle06Icon,
    position: [79, 22],
    href: '/toolkit/skills',
    description: 'Procedimientos para ejecutar tareas de principio a fin.',
    surface: 'bg-secondary/10',
    border: 'border-secondary/25',
    text: 'text-secondary',
  },
  {
    key: 'steerings',
    label: 'Steerings',
    icon: Shield01Icon,
    position: [86, 57],
    href: '/toolkit/steerings',
    description: 'Reglas que orientan el uso seguro y consistente de la IA.',
    surface: 'bg-success/10',
    border: 'border-success/25',
    text: 'text-success',
  },
  {
    key: 'models',
    label: 'Modelos',
    icon: CpuIcon,
    position: [70, 84],
    href: '/toolkit/modelos',
    description: 'Modelos evaluados, autorizados y documentados en VIA.',
    surface: 'bg-warning/10',
    border: 'border-warning/25',
    text: 'text-warning',
  },
  {
    key: 'mcp',
    label: 'MCP',
    icon: ServerStack01Icon,
    position: [38, 87],
    href: '/toolkit/mcp',
    description: 'Conectores MCP disponibles para agentes y aplicaciones.',
    surface: 'bg-accent/20',
    border: 'border-accent-bright/25',
    text: 'text-accent-bright',
  },
  {
    key: 'agents',
    label: 'Agentes',
    icon: BotIcon,
    position: [14, 66],
    href: '/toolkit/agentes',
    description: 'Agentes institucionales con propósito, owner y controles.',
    surface: 'bg-risk-medhigh/10',
    border: 'border-risk-medhigh/25',
    text: 'text-risk-medhigh',
  },
];
const INITIATIVES_KIND = {
  key: 'initiatives',
  label: 'Iniciativas',
  icon: GridViewIcon,
  position: [21, 25],
  href: '/registro',
  description: 'Aplicaciones e iniciativas de IA registradas y gobernadas en VIA.',
  surface: 'bg-chart-5/10',
  border: 'border-chart-5/25',
  text: 'text-chart-5',
};
const ECOSYSTEM_KINDS = [...KINDS, INITIATIVES_KIND];
const TOOLKIT_ROUTE_BY_KIND = {
  prompts: 'prompts',
  skills: 'skills',
  steerings: 'steerings',
  models: 'modelos',
  mcp: 'mcp',
  agents: 'agentes',
};

const Chip = ({ children }) => (
  <span className="px-2 py-0.5 rounded-md bg-secondary/10 text-secondary border border-secondary/20 text-[10px] font-medium">
    {children}
  </span>
);
const Row = ({ label, value }) => (
  <p className="text-xs mt-1">
    <span className="text-muted-foreground">{label}: </span>
    {value ?? '—'}
  </p>
);

function EcosystemNode({ kind, index, count, items, shouldReduceMotion }) {
  const stages =
    kind.key === 'initiatives'
      ? items.reduce((summary, item) => {
          const stage = item.stage || 'Sin etapa';
          summary[stage] = (summary[stage] || 0) + 1;
          return summary;
        }, {})
      : null;
  const totalUses =
    kind.key === 'initiatives' ? 0 : items.reduce((sum, item) => sum + Number(item.usage_count || 0), 0);

  return (
    <HoverCard openDelay={140} closeDelay={100}>
      <HoverCardTrigger asChild>
        <Link to={kind.href} aria-label={`Explorar ${kind.label}`}>
          <motion.div
            className="flex flex-col items-center"
            animate={shouldReduceMotion ? undefined : { y: [0, -6, 0, 4, 0] }}
            transition={{ duration: 5 + index * 0.55, delay: index * 0.22, repeat: Infinity, ease: 'easeInOut' }}
            whileHover={shouldReduceMotion ? undefined : { scale: 1.08 }}>
            <div
              className={cn(
                'relative flex size-16 items-center justify-center overflow-hidden rounded-full border bg-background ring-2 ring-background transition-colors',
                kind.border,
              )}>
              <span className={cn('absolute inset-0', kind.surface)} aria-hidden="true" />
              <HugeiconsIcon icon={kind.icon} className={cn('relative size-7', kind.text)} aria-hidden="true" />
            </div>
            <div className="mt-2 flex items-center gap-1.5 rounded-full border border-border/60 bg-background/90 px-2.5 py-1 backdrop-blur">
              <p className="text-[11px] font-bold">{kind.label}</p>
              <Badge
                variant="outline"
                className={cn('h-4 rounded-full border-0 bg-muted/60 px-1.5 text-[9px]', kind.text)}>
                {count}
              </Badge>
            </div>
          </motion.div>
        </Link>
      </HoverCardTrigger>
      <HoverCardContent side="top" sideOffset={12} align="center" className="w-72 border-border/80 p-0 shadow-none">
        <div className="flex items-center gap-3 border-b border-border/70 bg-muted/30 p-4">
          <span
            className={cn('flex size-10 items-center justify-center rounded-full border', kind.surface, kind.border)}>
            <HugeiconsIcon icon={kind.icon} className={cn('size-5', kind.text)} aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-bold">{kind.label}</p>
            <p className="text-xs text-muted-foreground">
              {count} {count === 1 ? 'elemento' : 'elementos'}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-3 p-4">
          <p className="text-xs leading-5 text-muted-foreground">{kind.description}</p>
          {stages ? (
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(stages)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([stage, amount]) => (
                  <div
                    key={stage}
                    className="flex items-center justify-between gap-2 rounded-lg bg-muted/50 px-2.5 py-2 text-xs">
                    <span className="text-muted-foreground">{stage}</span>
                    <span className="font-bold">{amount}</span>
                  </div>
                ))}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between rounded-lg bg-muted/50 px-2.5 py-2 text-xs">
                <span className="text-muted-foreground">Usos registrados</span>
                <span className="font-bold">{totalUses}</span>
              </div>
              {items.length > 0 && (
                <div>
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Más recientes
                  </p>
                  <div className="flex flex-col gap-1.5">
                    {items.slice(0, 3).map((item) => (
                      <p key={item.id} className="line-clamp-1 text-xs font-medium">
                        {item.title || item.name}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          <p className={cn('text-xs font-semibold', kind.text)}>Abrir catálogo →</p>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

export default function Registries({
  embedded = false,
  technicalKind = null,
  initialView = 'grid',
  ecosystemOnly = false,
}) {
  const [data, setData] = useState({});
  const [view, setView] = useState(initialView);
  const selectedKind = technicalKind || 'models';
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    const kindsToLoad = ecosystemOnly ? ECOSYSTEM_KINDS : KINDS;
    kindsToLoad.forEach(({ key }) => {
      const endpoint =
        key === 'initiatives'
          ? '/initiatives'
          : ecosystemOnly
            ? `/toolkit/${TOOLKIT_ROUTE_BY_KIND[key]}`
            : `/registry/${key}`;
      api.get(endpoint).then(({ data: result }) => {
        const rows = Array.isArray(result) ? result : result.items || [];
        setData((p) => ({ ...p, [key]: rows }));
      });
    });
  }, [ecosystemOnly]);

  return (
    <div className={cn('flex flex-col gap-8', !embedded && 'max-w-7xl mx-auto px-6 py-10')}>
      <MotionSection>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
              {ecosystemOnly ? 'Ecosistema de recursos IA' : 'Catálogo técnico'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
              {ecosystemOnly
                ? 'Vista integral de las iniciativas y los recursos publicados por la comunidad: prompts, skills, steerings, modelos, MCP y agentes.'
                : 'Inventario institucional de recursos de IA autorizados y sus relaciones dentro de VIA.'}
            </p>
            {ecosystemOnly && (
              <p className="mt-2 text-xs font-medium text-primary">
                Pasa el cursor sobre una esfera para explorar sus indicadores.
              </p>
            )}
          </div>
          {!ecosystemOnly && (
            <div className="flex gap-2">
              <Button
                variant={view === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('grid')}
                className={cn('text-xs rounded-lg', view === 'grid' && 'bg-primary text-primary-foreground')}
                aria-label="Vista de grilla">
                Grilla
              </Button>
              <Button
                variant={view === 'ecosystem' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('ecosystem')}
                className={cn('text-xs rounded-lg', view === 'ecosystem' && 'bg-primary text-primary-foreground')}
                aria-label="Vista de ecosistema">
                <HugeiconsIcon icon={GlobeIcon} className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
                Ecosistema
              </Button>
            </div>
          )}
        </div>
      </MotionSection>

      <MotionSection delay={0.03}>
        <div className="grid gap-4 md:grid-cols-3">
          <LifecycleCard
            icon={GitBranchIcon}
            title="Versionado obligatorio"
            description="Cada cambio conserva versión, dependencia, owner y aprobador; los consumidores pueden rastrear qué usaron."
          />
          <LifecycleCard
            icon={FileValidationIcon}
            title="Evaluación antes de promover"
            description="Un activo pasa de borrador a aprobado solo con evals, permisos y limitaciones explícitas."
          />
          <LifecycleCard
            icon={Calendar01Icon}
            title="Expiración y retiro"
            description="Revisión máxima semestral; sin owner o evidencia vigente el activo entra en deprecación."
          />
        </div>
      </MotionSection>

      {view === 'ecosystem' && (
        <MotionSection delay={0.05}>
          <Card className="border-border shadow-none rounded-2xl overflow-hidden">
            <CardContent className="p-0">
              <div className="relative flex h-[520px] w-full items-center justify-center overflow-hidden bg-gradient-panel">
                <div
                  className="absolute left-1/2 top-1/2 size-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/[0.04] blur-2xl"
                  aria-hidden="true"
                />
                <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
                  <motion.div
                    className="absolute size-64 rounded-full border border-dashed border-primary/20"
                    animate={shouldReduceMotion ? undefined : { rotate: 360 }}
                    transition={{ duration: 45, repeat: Infinity, ease: 'linear' }}
                  />
                  <motion.div
                    className="absolute size-96 rounded-full border border-dashed border-secondary/15"
                    animate={shouldReduceMotion ? undefined : { rotate: -360 }}
                    transition={{ duration: 65, repeat: Infinity, ease: 'linear' }}
                  />
                  <div className="absolute size-[28rem] rounded-full border border-border/20" />
                </div>

                <div className="relative z-10 flex flex-col items-center text-center">
                  <motion.div
                    className="relative mb-3 flex size-20 items-center justify-center overflow-hidden rounded-full border border-primary/30 bg-background ring-8 ring-primary/[0.04]"
                    animate={shouldReduceMotion ? undefined : { scale: [1, 1.04, 1] }}
                    transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}>
                    <span className="absolute inset-0 bg-primary/10" aria-hidden="true" />
                    <HugeiconsIcon
                      icon={ArtificialIntelligence05Icon}
                      className="relative size-9 text-primary"
                      aria-hidden="true"
                    />
                  </motion.div>
                  <p className="font-display text-lg font-bold">VIA</p>
                  <p className="text-xs text-muted-foreground">Ecosistema institucional</p>
                </div>

                {ECOSYSTEM_KINDS.map((kind, i) => {
                  const count = data[kind.key]?.length || 0;

                  return (
                    <div
                      key={kind.key}
                      className="absolute z-10 flex flex-col items-center"
                      style={{
                        left: `${kind.position[0]}%`,
                        top: `${kind.position[1]}%`,
                        transform: 'translate(-50%, -50%)',
                      }}>
                      <EcosystemNode
                        kind={kind}
                        index={i}
                        count={count}
                        items={data[kind.key] || []}
                        shouldReduceMotion={shouldReduceMotion}
                      />
                    </div>
                  );
                })}

                <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
                  {ECOSYSTEM_KINDS.map((kind, i) => {
                    const cx = 50;
                    const cy = 50;
                    const x2 = kind.position[0];
                    const y2 = kind.position[1];
                    return (
                      <motion.line
                        key={kind.key}
                        x1={`${cx}%`}
                        y1={`${cy}%`}
                        x2={`${x2}%`}
                        y2={`${y2}%`}
                        stroke="hsl(var(--border))"
                        strokeWidth="1.5"
                        strokeDasharray="5 6"
                        initial={shouldReduceMotion ? undefined : { pathLength: 0, opacity: 0 }}
                        animate={shouldReduceMotion ? undefined : { pathLength: 1, opacity: [0.2, 0.7, 0.2] }}
                        transition={{
                          pathLength: { duration: 1.2, delay: i * 0.12 },
                          opacity: { duration: 3.5, delay: i * 0.2, repeat: Infinity },
                        }}
                      />
                    );
                  })}
                </svg>
              </div>
            </CardContent>
          </Card>
        </MotionSection>
      )}

      {view === 'grid' && (
        <Tabs value={selectedKind}>
          {!technicalKind && (
            <TabsList className="bg-muted/50 flex-wrap h-auto rounded-xl p-1">
              {KINDS.map((k) => (
                <TabsTrigger
                  key={k.key}
                  value={k.key}
                  data-testid={`registry-tab-${k.key}`}
                  className="text-xs sm:text-sm rounded-lg gap-1.5">
                  <HugeiconsIcon icon={k.icon} className="h-3.5 w-3.5" aria-hidden="true" />
                  {k.label}
                  {data[k.key] && (
                    <Badge variant="outline" className="text-[9px] ml-1 h-4 px-1.5 bg-muted/50">
                      {data[k.key].length}
                    </Badge>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          )}

          {KINDS.filter((k) => !technicalKind || k.key === technicalKind).map((k) => (
            <TabsContent key={k.key} value={k.key} className="mt-5">
              {!data[k.key] ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} className="h-44 rounded-2xl" />
                  ))}
                </div>
              ) : (
                <StaggerContainer className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" stagger={0.04}>
                  {data[k.key].map((r) => (
                    <StaggerItem key={r.id}>
                      <Card
                        className="border-border shadow-none rounded-2xl flex flex-col transition-colors duration-200 hover:border-primary/30 hover:bg-muted/10"
                        data-testid={`registry-item-${r.id}`}>
                        <CardContent className="p-5 flex flex-col flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="h-9 w-9 rounded-lg bg-primary/8 text-primary flex items-center justify-center shrink-0">
                                <HugeiconsIcon icon={k.icon} className="h-4 w-4" aria-hidden="true" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-display font-bold text-sm leading-tight truncate">{r.name}</p>
                                <p className="text-[11px] text-muted-foreground truncate">
                                  {r.provider || r.owner || r.purpose}
                                </p>
                              </div>
                            </div>
                            <StatusBadge status={r.status || r.approval_status} />
                          </div>

                          <div className="mt-3 flex-1">
                            {k.key === 'models' && (
                              <>
                                <Row label="Versión" value={r.version} />
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {(r.capabilities || []).map((c) => (
                                    <Chip key={c}>{c}</Chip>
                                  ))}
                                </div>
                                <Row label="Restricciones" value={r.restrictions} />
                                <Row label="Riesgos conocidos" value={r.known_risks} />
                                <Row label="Iniciativas que lo usan" value={r.initiatives_count} />
                              </>
                            )}
                            {k.key === 'prompts' && (
                              <>
                                <Row label="Propósito" value={r.purpose} />
                                <Row label="Modelo" value={r.related_model} />
                                <Row label="Versión" value={r.version} />
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {(r.variables || []).map((v) => (
                                    <Chip key={v}>{`{${v}}`}</Chip>
                                  ))}
                                </div>
                                <Row label="Evaluaciones" value={r.evaluations} />
                              </>
                            )}
                            {k.key === 'mcp' && (
                              <>
                                <Row label="Propósito" value={r.purpose} />
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {(r.exposed_tools || []).map((t) => (
                                    <Chip key={t}>{t}</Chip>
                                  ))}
                                </div>
                                <Row label="Datos accesibles" value={r.accessible_data} />
                                <Row label="Autenticación" value={r.authentication} />
                                <div className="mt-2">
                                  <RiskBadge level={r.risk} />
                                </div>
                              </>
                            )}
                            {k.key === 'skills' && (
                              <>
                                <Row label="Descripción" value={r.description} />
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {(r.systems || []).map((s) => (
                                    <Chip key={s}>{s}</Chip>
                                  ))}
                                </div>
                                <Row label="Permisos" value={r.permissions} />
                                <Row label="Versión" value={r.version} />
                              </>
                            )}
                            {k.key === 'agents' && (
                              <>
                                <Row label="Propósito" value={r.purpose} />
                                <Row label="Modelo" value={r.model} />
                                <Row label="Autonomía" value={r.autonomy_level} />
                                <Row label="Límites" value={r.limits} />
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {(r.controls || []).map((c) => (
                                    <Chip key={c}>{c}</Chip>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-3 pt-3 border-t border-border">
                            Owner: {r.owner || 'Dirección de Gobernanza de IA'}
                          </p>
                        </CardContent>
                      </Card>
                    </StaggerItem>
                  ))}
                </StaggerContainer>
              )}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}

function LifecycleCard({ icon, title, description }) {
  return (
    <Card className="rounded-2xl border-dashed">
      <CardContent className="flex gap-4 p-5">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <HugeiconsIcon icon={icon} className="size-5" aria-hidden="true" />
        </div>
        <div>
          <p className="text-sm font-semibold">{title}</p>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>
        </div>
      </CardContent>
    </Card>
  );
}
